import { fetchWithAuth } from '../utils/tokenRefresh';
import { getApiEndpoint } from '../config/apiEndpoints';

export interface Word {
  uuid: string;
  word: string;
  is_easy_eligible: boolean;
  is_normal_eligible: boolean;
  definition: string;
  etymology: string;
}

export async function listWords(params: {
  limit?: number;
  last_key?: string | null;
  filter?: string;
  q?: string;
}): Promise<{ words: Word[]; last_key: string | null }> {
  const searchParams = new URLSearchParams();
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.last_key) searchParams.set('last_key', params.last_key);
  if (params.filter) searchParams.set('filter', params.filter);
  if (params.q) searchParams.set('q', params.q);
  const url = getApiEndpoint(`/admin/words?${searchParams.toString()}`);
  const response = await fetchWithAuth(url);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to list words');
  return data;
}

export async function createWord(word: Omit<Word, 'uuid'>): Promise<Word> {
  const response = await fetchWithAuth(getApiEndpoint('/admin/words'), {
    method: 'POST',
    body: JSON.stringify(word),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create word');
  return data.word;
}

export async function updateWord(uuid: string, updates: Partial<Word>): Promise<Word> {
  const response = await fetchWithAuth(getApiEndpoint(`/admin/words/${uuid}`), {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update word');
  return data.word;
}

export async function upsertWordDefinition(
  wordText: string,
  definition: string,
  etymology: string,
): Promise<Word> {
  const result = await listWords({ q: wordText, limit: 50 });
  const match = result.words.find((w) => w.word === wordText.toLowerCase());
  if (match) {
    return updateWord(match.uuid, { definition, etymology });
  }
  return createWord({
    word: wordText.toLowerCase(),
    is_easy_eligible: false,
    is_normal_eligible: false,
    definition,
    etymology,
  });
}

export async function deleteWord(uuid: string): Promise<void> {
  const response = await fetchWithAuth(getApiEndpoint(`/admin/words/${uuid}`), {
    method: 'DELETE',
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete word');
  }
}
